import type { GameState, Side, LogEntry, BotDifficulty, Card } from './types';
import { AI_PARAMS } from './config';
import {
  canAfford,
  playCard,
  playChampion,
  moveUnit,
  resolveShowdown,
  startTurn,
  checkWinner,
  getEnergyCost,
  isSpellCard,
  isGearCard,
  getEffectiveMight,
  getUnitsInBase,
  getUnitsAtBattlefield,
} from './engine';

export function runBotTurn(
  state: GameState,
  difficulty: BotDifficulty,
  logs: LogEntry[]
): void {
  const params = AI_PARAMS[difficulty];
  const side: Side = 'opponent';
  const bot = state.opponent;

  // Start turn
  state.turnNumber++;
  startTurn(state, side, logs);

  if (state.winner) return;

  // 1. Play champion if affordable and not yet played
  if (bot.championAvailable && bot.championCard && canAfford(bot, bot.championCard)) {
    playChampion(state, side, logs);
  }

  // 2. Play cards from hand
  let plays = 0;
  let changed = true;
  while (changed && plays < params.maxPlaysPerTurn && !state.winner) {
    changed = false;

    // Sort hand by priority: units first (higher might = better), then spells
    const playableIndices = bot.hand
      .map((card, i) => ({ card, index: i }))
      .filter(({ card }) => canAfford(bot, card))
      .sort((a, b) => {
        // Prefer units over spells/gear
        const aIsUnit = !isSpellCard(a.card) && !isGearCard(a.card) ? 0 : 1;
        const bIsUnit = !isSpellCard(b.card) && !isGearCard(b.card) ? 0 : 1;
        if (aIsUnit !== bIsUnit) return aIsUnit - bIsUnit;
        return getEnergyCost(b.card) - getEnergyCost(a.card); // prefer expensive cards
      });

    for (const { card, index } of playableIndices) {
      if (plays >= params.maxPlaysPerTurn) break;

      // For spells, check chance
      if (isSpellCard(card) && Math.random() > params.playSpellChance) continue;

      if (playCard(state, side, index, logs)) {
        plays++;
        changed = true;
        break; // re-evaluate hand after each play
      }
    }
  }

  if (state.winner) return;

  // 3. Move units to battlefields and attack
  let attacks = 0;
  const baseUnits = getUnitsInBase(state, side).filter((u) => !u.exhausted);

  // Decide where to attack
  for (const bf of state.battlefields) {
    if (attacks >= params.maxAttacksPerTurn) break;
    if (state.winner) break;

    // Check if there's opportunity to attack
    const myUnitsHere = getUnitsAtBattlefield(state, side, bf.id);
    const enemySide: Side = 'player';
    const enemyUnitsHere = getUnitsAtBattlefield(state, enemySide, bf.id);

    // Calculate if we should attack
    const myMight = myUnitsHere.reduce((sum, u) => sum + getEffectiveMight(u, 'attacking'), 0);
    const enemyMight = enemyUnitsHere.reduce((sum, u) => sum + getEffectiveMight(u, 'defending'), 0);

    // Move base units to the battlefield if we need reinforcement
    const availableBase = baseUnits.filter((u) => !u.exhausted);
    if (Math.random() < params.moveChance) {
      const unitsToMove = params.attackPreference === 'strongest'
        ? availableBase.sort((a, b) => getEffectiveMight(b) - getEffectiveMight(a))
        : availableBase;

      let moved = 0;
      for (const unit of unitsToMove) {
        if (moved >= 3) break; // max 3 units per battlefield move
        if (unit.exhausted) continue;

        // Check if this battlefield is worth attacking
        const wouldHaveMight = myMight + getEffectiveMight(unit, 'attacking');
        if (bf.controlledBy !== side && (wouldHaveMight > enemyMight * 0.8 || enemyMight === 0)) {
          moveUnit(state, side, unit.instanceId, bf.id, logs);
          moved++;
        }
      }
    }

    // Now check again after moving
    const updatedMyUnits = getUnitsAtBattlefield(state, side, bf.id);
    const updatedEnemyUnits = getUnitsAtBattlefield(state, enemySide, bf.id);

    if (updatedMyUnits.length > 0 && bf.controlledBy !== side) {
      const totalAttMight = updatedMyUnits.reduce((s, u) => s + (u.stunned ? 0 : getEffectiveMight(u, 'attacking')), 0);
      const totalDefMight = updatedEnemyUnits.reduce((s, u) => s + getEffectiveMight(u, 'defending'), 0);

      // Attack if we have advantage or it's undefended
      const shouldAttack =
        updatedEnemyUnits.length === 0 ||
        totalAttMight >= totalDefMight * (difficulty === 'facile' ? 0.6 : 0.8);

      if (shouldAttack) {
        resolveShowdown(state, side, bf.id, logs);
        attacks++;
        if (checkWinner(state)) {
          state.winner = checkWinner(state);
          return;
        }
      }
    }
  }

  // End bot turn
  if (!state.winner) {
    state.currentTurn = 'player';
    state.turnNumber++;
    startTurn(state, 'player', logs);
    if (checkWinner(state)) {
      state.winner = checkWinner(state);
    }
  }
}
